import {
  Skeleton,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { QueryStatus } from "@reduxjs/toolkit/query";

import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useNumberSerialiser } from "hooks/serialisers/numbers";
import React from "react";

import { Period } from "hooks/periodIndex";
import {
  ProductionMixResultRead,
  SingleMixSummary,
  useSummarisePlanResultsMutation,
} from "src/store/api/generatedApi";
import { PlanId } from "../boundary/PlanId";
import { originalMixPrice } from "./utils";

export type Detail = "cu" | "price_diff";

export const MIX_SUMMARY_WIDTH = 120;

interface Props {
  planId: PlanId;
  period: Period;
  mixId: number;
  originalCost: number;
  details: Detail[];
  preloadedSummary?: SingleMixSummary | null;
  presentation?: "table" | "header";
}

const NoDiff = () => {
  return (
    <Typography
      component="span"
      variant="body2Mono"
      sx={{
        paddingX: 1,
      }}
    >
      -
    </Typography>
  );
};

export const MixSummaryCell = ({
  originalCost,
  planId,
  period,
  mixId,
  details,
  preloadedSummary,
  presentation = "table",
}: Props) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();

  const { format: priceFormat } = useNumberSerialiser({
    decimalPlaces: 2,
    min: 0,
  });
  const unit = useUnitsFormatter(false);

  const detailsSet = React.useMemo(() => {
    return new Set(details);
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- serialized form used as stable dep key to avoid re-computation when array identity changes but values don't
  }, [details.slice(0).sort().join(".")]);

  const originalPrice = (
    <Typography component="span" variant="body2Mono">
      {priceFormat(originalCost)}
      <Typography
        component="span"
        variant="body2Mono"
        sx={{ color: theme.palette.text.disabled }}
      >
        {unit("specific_cost")}
      </Typography>
    </Typography>
  );
  const editedPrice = (
    <DynamicSummary
      mixId={mixId}
      planId={planId}
      period={period}
      originalCost={originalCost}
      details={detailsSet}
      preloadedSummary={preloadedSummary}
    />
  );

  return presentation === "header" ? (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
    >
      <Tooltip title={t("original")}>{originalPrice}</Tooltip>
      <Tooltip title={t("modified")}>
        <Stack
          alignItems="center"
          sx={{ width: MIX_SUMMARY_WIDTH, flexShrink: 0 }}
        >
          {editedPrice}
        </Stack>
      </Tooltip>
    </Stack>
  ) : (
    <Stack alignItems="center" justifyContent="center" height="100%" gap={1}>
      {originalPrice}
      {editedPrice}
    </Stack>
  );
};

interface MixPriceComparisonProps {
  planId: PlanId;
  period: Period;
  mixId: number;
  originalMixes: ProductionMixResultRead[];
}

export const renderMixPriceComparison = ({
  planId,
  period,
  mixId,
  originalMixes,
}: MixPriceComparisonProps): React.ReactNode => {
  const originalMix = originalMixes.find((mix) => mix.mix.id === mixId);
  const originalCost =
    originalMix === undefined ? null : originalMixPrice(originalMix);
  if (originalCost === null) return null;

  return (
    <MixSummaryCell
      planId={planId}
      period={period}
      mixId={mixId}
      originalCost={originalCost}
      details={["price_diff"]}
      presentation="header"
    />
  );
};

interface EditedCostProps {
  cost: number;
  originalCost: number;
}

const EditedCost = ({ cost, originalCost }: EditedCostProps) => {
  const theme = useTheme();
  const { format: priceFormat } = useNumberSerialiser({
    decimalPlaces: 2,
    min: 0,
  });
  const unit = useUnitsFormatter(false);

  if (cost.toFixed(2) === originalCost.toFixed(2)) {
    return <NoDiff />;
  }

  const backgroundColor: Theme["palette"]["success"]["main"] = (() => {
    switch (true) {
      case cost < originalCost:
        return theme.palette.success.main;
      case cost > originalCost:
        return theme.palette.error.main;
      default:
        return theme.palette.grey[600];
    }
  })();
  return (
    <Typography
      component="span"
      variant="body2Mono"
      sx={{
        backgroundColor,
        color: theme.palette.common.white,
        paddingX: 1,
      }}
    >
      {priceFormat(cost)}
      <Typography component="span" variant="body2Mono">
        {unit("specific_cost")}
      </Typography>
    </Typography>
  );
};

interface DynamicSummaryProps {
  mixId: number;
  planId: PlanId;
  period: Period;
  originalCost: number;
  details: Set<Detail>;
  preloadedSummary?: SingleMixSummary | null;
}

type DetailProps =
  | {
      detail: "price_diff";
      originalCost: number;
      displayCost: number;
    }
  | {
      detail: "cu";
      copperChemistryWeightPercentLoc: number;
    };

const Detail = ({ ...props }: DetailProps) => {
  const unitFormatter = useUnitsFormatter(false);

  switch (props.detail) {
    case "price_diff": {
      const { displayCost, originalCost } = props;
      return <EditedCost cost={displayCost} originalCost={originalCost} />;
    }
    case "cu": {
      const { copperChemistryWeightPercentLoc } = props;
      return (
        <Typography variant="body2Mono" sx={{ padding: 0.5 }}>
          {"Cu"}&nbsp;
          <Typography
            fontWeight="fontWeightBold"
            component="span"
            variant="body2Mono"
          >
            {copperChemistryWeightPercentLoc.toFixed(3)}
          </Typography>
          {unitFormatter("mass_fraction")}
        </Typography>
      );
    }
  }
};

const DynamicSummary = ({
  planId,
  period,
  mixId,
  originalCost,
  details,
  preloadedSummary,
}: DynamicSummaryProps) => {
  const theme = useTheme();
  const [, summarisePlanResultsMutationResult] =
    useSummarisePlanResultsMutation({
      fixedCacheKey: `${planId}-${period}`,
    });

  const mixSummary =
    preloadedSummary !== undefined
      ? preloadedSummary
      : summarisePlanResultsMutationResult.status === QueryStatus.fulfilled
        ? summarisePlanResultsMutationResult.data.mixes.find(
            (result) => result.original_mix_id === mixId
          )?.mix.summary
        : undefined;

  if (mixSummary !== undefined) {
    if (mixSummary) {
      return (
        <>
          {[...details.values()].map((detail): React.JSX.Element | null => {
            switch (detail) {
              case "cu": {
                const copperChemistry = mixSummary.chemistry.find(
                  (constraint) => constraint.constraint_name === "Cu"
                );
                if (copperChemistry) {
                  return (
                    <Detail
                      key="cu"
                      detail="cu"
                      copperChemistryWeightPercentLoc={
                        copperChemistry.weight_percent_loc
                      }
                    />
                  );
                } else {
                  return null;
                }
              }
              case "price_diff": {
                return (
                  <Detail
                    key="price_diff"
                    detail="price_diff"
                    displayCost={mixSummary.cost.display_cost_per_tonne}
                    originalCost={originalCost}
                  />
                );
              }
              default: {
                return null;
              }
            }
          })}
        </>
      );
    }
    return null;
  } else if (
    preloadedSummary === undefined &&
    (summarisePlanResultsMutationResult.status === QueryStatus.uninitialized ||
      summarisePlanResultsMutationResult.status === QueryStatus.pending)
  ) {
    return (
      <>
        {[...details.values()].map((detail) => (
          <Skeleton
            key={detail}
            variant="text"
            sx={{
              fontSize: theme.typography.body2Mono.fontSize,
              padding: 0.5,
              width: 36,
            }}
          />
        ))}
      </>
    );
  } else {
    return null;
  }
};
